/* eslint-disable no-magic-numbers */
import { getLogger } from '@trimble/logging';
import workerpool, { Pool } from 'workerpool';

import { QuantizedMeshData } from '../types/quantized-mesh-data';
import type {
  ConstructedGeometryBuffers,
  ConstructGeometryWorkerParams,
  TglShiftGridEntry,
} from './construct-geometry.types';

/*
 * This class interfaces with the workerpool library to manage a pool of workers.
 * It allows multiple decoding tasks to be processed in parallel,
 * reducing the time taken to decode large datasets.
 */
/**
 * Default geometry worker count. Each geometry worker loads its own copy of the
 * TGL WASM module, so start-up time and memory grow with every worker added.
 * Four workers already build ~200 heavy (16k-vertex) tiles per second, far
 * faster than tiles arrive over the network, so more cores add cost without
 * a visible gain.
 */
export const DEFAULT_MAX_GEOMETRY_WORKERS = 4;

function defaultDecodeWorkerCount(): number {
  return Math.max(1, Math.floor(navigator.hardwareConcurrency) - 2);
}

type TglState = 'uninitialized' | 'initializing' | 'ready' | 'failed';

export class QuantizedMeshWorkerPool {
  private readonly decodePool: Pool;
  private readonly geometryPool: Pool;
  private readonly geometryPoolSize: number;
  private tglState: TglState = 'uninitialized';
  private geometryPoolReady: Promise<void> = Promise.resolve();

  constructor(
    private readonly workerScriptUrl: string,
    private readonly poolSize = defaultDecodeWorkerCount(),
    geometryPoolSize = Math.min(
      DEFAULT_MAX_GEOMETRY_WORKERS,
      defaultDecodeWorkerCount()
    )
  ) {
    this.poolSize = Math.max(1, poolSize); // Ensure at least one worker
    this.geometryPoolSize = Math.max(1, Math.floor(geometryPoolSize));

    this.decodePool = this.createPool(this.poolSize);
    this.geometryPool = this.createPool(this.geometryPoolSize);
  }

  /**
   * Starts loading TGL into every geometry worker. Geometry requests made
   * while this runs wait for it rather than falling back to the main thread.
   * Never rejects: if initialization fails, geometry requests resolve to null
   * and callers build on the main thread instead.
   */
  public async initTglWorker(
    csib: ArrayBuffer,
    shiftGrids?: Map<string, ArrayBuffer>
  ): Promise<void> {
    const shiftGridEntries: TglShiftGridEntry[] | undefined = shiftGrids
      ? Array.from(shiftGrids.entries()).map(([name, data]) => ({
          name,
          data: new Uint8Array(data.slice(0)),
        }))
      : undefined;

    this.tglState = 'initializing';
    this.geometryPoolReady = this.initializePoolTgl(
      csib.slice(0),
      shiftGridEntries
    ).then(
      () => {
        this.tglState = 'ready';
      },
      (err: unknown) => {
        this.tglState = 'failed';
        getLogger().error(
          'Failed to initialize TGL in geometry workers; building geometry on the main thread instead:',
          err
        );
      }
    );
    await this.geometryPoolReady;
  }

  private async initializePoolTgl(
    csib: ArrayBuffer,
    shiftGridEntries: TglShiftGridEntry[] | undefined
  ): Promise<void> {
    await Promise.all(
      Array.from({ length: this.geometryPoolSize }, () =>
        this.geometryPool.exec<
          (
            csibBuffer: ArrayBuffer,
            grids?: TglShiftGridEntry[]
          ) => Promise<void>
        >('initTglWorker', [csib, shiftGridEntries])
      )
    );
  }

  /**
   * True once TGL initialization has started and has not failed. Geometry
   * requests may still be waiting for it to finish.
   */
  public isGeometryWorkerAvailable(): boolean {
    return this.tglState === 'initializing' || this.tglState === 'ready';
  }

  public get maxGeometryWorkers(): number {
    return this.geometryPoolSize;
  }

  /**
   * Submits data to be decoded by an available worker.
   * @param {*} dataToDecode The data needed for decoding (e.g., ArrayBuffer of the tile).
   * Ensure this data is structured cloneable or an ArrayBuffer for transfer.
   * @returns {Promise<object>} A promise that resolves with the decoded data object,
   * or rejects with an error.
   */
  public async decode(
    dataToDecode: ArrayBuffer
  ): Promise<QuantizedMeshData | null> {
    try {
      const result = await this.decodePool.exec<
        (arrayBuffer: ArrayBuffer) => Promise<QuantizedMeshData>
      >('decode', [dataToDecode], { transfer: [dataToDecode] });
      return result;
    } catch (err) {
      getLogger().error(
        'An error occurred during decoding in the worker:',
        err
      );
      return null;
    }
  }

  public async constructGeometry(
    params: ConstructGeometryWorkerParams,
    abortSignal?: AbortSignal
  ): Promise<ConstructedGeometryBuffers | null> {
    await this.geometryPoolReady;
    if (this.tglState !== 'ready' || abortSignal?.aborted) {
      return null;
    }

    try {
      // A task aborted mid-run is left to finish and its result dropped.
      // Cancelling it would terminate the worker, and the replacement would
      // need TGL loaded again, which takes longer than the task itself.
      const result = await this.geometryPool.exec<
        (input: ConstructGeometryWorkerParams) => ConstructedGeometryBuffers
      >('constructGeometry', [params]);
      return abortSignal?.aborted ? null : result;
    } catch (err) {
      getLogger().error(
        'An error occurred during geometry construction in the worker:',
        err
      );
      return null;
    }
  }

  /**
   * Terminates all workers in the pool. Call this when the pool is no longer needed.
   */
  public terminate(): void {
    void this.decodePool.terminate(true);
    void this.geometryPool.terminate(true);
  }

  private createPool(maxWorkers: number): Pool {
    return workerpool.pool(this.workerScriptUrl, { maxWorkers });
  }
}
